/** Pure helpers for the Discovery settings screen (contract §1). */
import type { SettingField, SettingsResponse, SettingValue } from '../api/types';

export function settingEquals(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const x = Array.isArray(a) ? a : [];
    const y = Array.isArray(b) ? b : [];
    return x.length === y.length && x.every((v, i) => v === y[i]);
  }
  if ((a === null || a === undefined || a === '') && (b === null || b === undefined || b === '')) return true;
  return a === b;
}

export interface SettingChange {
  key: string;
  label: string;
  group: string;
  from: SettingValue;
  to: SettingValue;
}

/** What Save would send: every field whose draft differs from the saved value, in field order. */
export function settingsDiff(
  fields: SettingField[],
  saved: Record<string, SettingValue>,
  draft: Record<string, SettingValue>,
): SettingChange[] {
  const out: SettingChange[] = [];
  for (const f of fields) {
    if (!(f.key in draft)) continue;
    const from = saved[f.key] ?? null;
    const to = draft[f.key] ?? null;
    if (!settingEquals(from, to)) out.push({ key: f.key, label: f.label, group: f.group, from, to });
  }
  return out;
}

export function changesToValues(changes: SettingChange[]): Record<string, SettingValue> {
  return Object.fromEntries(changes.map((c) => [c.key, c.to]));
}

/** Fields in their groups, groups in first-seen order. */
export function groupSettingFields(fields: SettingField[]): { group: string; fields: SettingField[] }[] {
  const groups = new Map<string, SettingField[]>();
  for (const f of fields) {
    const list = groups.get(f.group) ?? [];
    list.push(f);
    groups.set(f.group, list);
  }
  return [...groups.entries()].map(([group, list]) => ({ group, fields: list }));
}

export function formatSettingValue(field: Pick<SettingField, 'type' | 'max'> | undefined, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (field?.type === 'percent' && typeof value === 'number') {
    // Settings store percents as written (minAudiencePct = 30); a 0–1 field is scaled.
    return field.max != null && field.max <= 1 ? `${Math.round(value * 100)}%` : `${value}%`;
  }
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Client checks mirroring the server's (it still validates and answers 400 fieldErrors). */
export function validateSetting(field: SettingField, value: SettingValue): string | null {
  if (field.type === 'integer' || field.type === 'number' || field.type === 'percent') {
    if (value === null || value === '' || typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number';
    if (field.type === 'integer' && !Number.isInteger(value)) return 'Enter a whole number';
    if (field.min != null && value < field.min) return `Must be at least ${field.min}`;
    if (field.max != null && value > field.max) return `Must be at most ${field.max}`;
  }
  return null;
}

export function validateSettings(fields: SettingField[], draft: Record<string, SettingValue>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const msg = validateSetting(f, draft[f.key] ?? null);
    if (msg) errors[f.key] = msg;
  }
  return errors;
}

export function isSettingsResponse(raw: unknown): raw is SettingsResponse {
  return (
    !!raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as SettingsResponse).fields) &&
    typeof (raw as SettingsResponse).values === 'object'
  );
}

/** Splits a typed tag on commas so pasting "a, b, c" adds three. */
export function parseTags(input: string): string[] {
  return input.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
}

export function addTags(list: string[], input: string): string[] {
  const seen = new Set(list.map((t) => t.toLowerCase()));
  const next = [...list];
  for (const t of parseTags(input)) {
    if (!seen.has(t.toLowerCase())) {
      seen.add(t.toLowerCase());
      next.push(t);
    }
  }
  return next;
}
